import type { TicketChannelV3Condition as TicketChannelV3ConditionType } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  MESSAGE_CHANNELS_V3,
  type MessageChannelV3,
} from "@redotech/redo-model/advanced-flow/rules-v3/types";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as styles from "./conditions.module.css";

const CHANNEL_DISPLAY_NAMES: Record<MessageChannelV3, string> = {
  gmail: "Gmail",
  outlook: "Outlook",
  redoChat: "Chat",
  facebook: "Facebook",
  facebookComments: "Facebook Comments",
  instagram: "Instagram",
  instagramComments: "Instagram Comments",
  sms: "SMS",
  voice: "Voice",
  attentive: "Attentive",
  postscript: "Postscript",
  tiktok: "TikTok",
  contactForm: "Contact Form",
};

const channelItems: RedoListItem<MessageChannelV3>[] = MESSAGE_CHANNELS_V3.map(
  (channel) => ({
    id: channel,
    type: "text" as const,
    text: CHANNEL_DISPLAY_NAMES[channel],
    value: channel,
  }),
);

export const TicketChannelV3Condition = memo(function TicketChannelV3Condition({
  condition,
  onChange,
}: {
  condition: TicketChannelV3ConditionType;
  onChange: (updated: TicketChannelV3ConditionType) => void;
}) {
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems = useMemo(() => {
    return (condition.channels || []).map((channel) => ({
      id: channel,
      type: "text" as const,
      text: CHANNEL_DISPLAY_NAMES[channel],
      value: channel,
    }));
  }, [condition.channels]);

  const displayText =
    (condition.channels?.length || 0) > 0
      ? condition.channels!.length === 1 && condition.channels![0]
        ? CHANNEL_DISPLAY_NAMES[condition.channels![0]!]
        : `${condition.channels!.length} channels selected`
      : "Select channels";

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xxs" textColor="tertiary">
        Channel
      </Text>
      <RedoFilterDropdownAnchor
        className={styles.fullWidthButton}
        color="primary"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        ref={setDropdownButtonRef}
        text={displayText}
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={channelItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, channels: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
